import {Box} from '@mantine/core';
import React from 'react';

import {ANALYSIS_TOOL_SPACING} from 'analysis-tool/AnalysisToolConstants';
import {assert} from 'Asserts';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import {assertPipelineStepRawDataIsKind, PipelineStep} from 'pipelines/PipelineTypes';
import {resolveOutputStepData} from 'pipelines/PipelineViewUtils';
import {ScratchPlateContent} from 'pipelines/plates/ScratchPlateTypes';
import ScratchPlateViewerPane from 'pipelines/plates/ScratchPlateViewerPane';

import DataViewContext, {useDataViewContext} from './DataViewContext';
import DataViewPane from './DataViewPane';

export interface PlateViewProps {
  /** The plate to render. When omitted, it is resolved from the analysisView in DataViewContext. */
  plate?: ScratchPlateContent;
}

/**
 * Render view for scratch plates.
 *
 * Can be used with a `plate` prop to render the provided plate directly, or with no
 * props to resolve the plate from the analysisView in DataViewContext.
 */
export default function PlateView({plate}: PlateViewProps = {}): JSX.Element {
  if (plate != null) {
    return <PlateViewWithPlate plate={plate} />;
  }
  return <PlateViewWithAnalysisView />;
}

function PlateViewWithPlate({plate}: {plate: ScratchPlateContent}): JSX.Element {
  const dataViewContextValue = useDataViewContext();

  return (
    <DataViewContext.Provider value={dataViewContextValue}>
      <DataViewPane>
        <Box p={ANALYSIS_TOOL_SPACING} h="100%">
          <ScratchPlateViewerPane scratchPlateId={plate.id} />
        </Box>
      </DataViewPane>
    </DataViewContext.Provider>
  );
}

function PlateViewWithAnalysisView(): JSX.Element {
  const dataViewContextValue = useDataViewContext();
  const {analysisView, steps} = dataViewContextValue;

  assert(analysisView != null, 'analysisView must be provided in context when using PlateView');

  const lastNode = analysisView.lineage[analysisView.lineage.length - 1];
  const stepOutputReference = lastNode.stepOutputReference!;
  const stepsArray = steps ?? ([] as ReadonlyArray<PipelineStep>);
  const outputStepData = resolveOutputStepData(stepsArray, {
    step: lastNode.step,
    stepOutputReference,
  });
  const plate = outputStepData.rawDatas[stepOutputReference.rawDataIndex!];

  assertPipelineStepRawDataIsKind(plate, PipelineStepDataKind.PLATE);
  return <PlateViewWithPlate plate={plate} />;
}
